import { useEffect } from 'react';
import cn from 'classnames';
import ChevronLeftIcon from '@/shared/assets/icons/chevron-left.svg';
import ArrowRightIcon from '@/shared/assets/icons/arrow-right.svg';
import CloseIcon from '@/shared/assets/icons/close.svg';
import { Svg } from '@/shared/ui/Svg';
import type { IContentStep } from '@/shared/types/lesson';
import {
  getLessonStepTheme,
  getLessonStepTitle,
  LESSON_STEP_THEME_COLORS,
} from './stepTheme';

type SessionStepsDrawerProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  steps: IContentStep[];
  currentStepOrder: number;
  onSelectStep: (order: number) => void;
};

export const SessionStepsDrawer = ({
  isOpen,
  onOpen,
  onClose,
  steps,
  currentStepOrder,
  onSelectStep,
}: SessionStepsDrawerProps) => {
  const sorted = [...steps].sort((a, b) => a.order - b.order);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open session steps"
        aria-expanded={isOpen}
        className={cn(
          'group fixed right-0 top-1/2 z-40 flex h-[110px] w-[29px] -translate-y-1/2 cursor-pointer items-center justify-center overflow-hidden rounded-l-2xl border border-r-0 border-[#211D55] bg-[#211D55] px-1.5 py-3 text-white transition-[width,box-shadow,transform,opacity] duration-300 ease-out hover:w-[61px] hover:shadow-[0_2.67px_6px_rgba(33,29,85,0.35)]',
          isOpen && 'pointer-events-none translate-x-full opacity-0',
        )}
      >
        <span className="flex flex-nowrap items-center gap-2">
          <span className="rotate-180 whitespace-nowrap text-[14px] font-medium leading-[120%] [writing-mode:vertical-rl]">
            Session steps
          </span>
          <span className="flex h-6 w-0 shrink-0 items-center justify-center overflow-hidden transition-[width] duration-300 ease-out group-hover:w-6">
            <Svg Icon={ChevronLeftIcon} className="h-6 w-6 shrink-0 text-white" />
          </span>
        </span>
      </button>

      {isOpen && (
        <button
          type="button"
          aria-label="Close session steps overlay"
          className="fixed inset-0 z-40 cursor-default bg-transparent"
          onClick={onClose}
        />
      )}

      <aside
        role="navigation"
        aria-label="Session Steps"
        aria-hidden={!isOpen}
        className={cn(
          'fixed bottom-0 right-0 z-50 flex h-[min(100vh,820px)] w-[min(100vw,510px)] flex-col bg-white shadow-[-8px_0_32px_rgba(30,30,30,0.12)] transition-transform duration-300 ease-out',
          'rounded-tl-[20px] sm:rounded-tl-[24px]',
          isOpen ? 'translate-y-0' : 'pointer-events-none translate-y-full',
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 px-7 pb-5 pt-7 sm:px-8">
          <h2 className="text-[28px] font-medium leading-[120%] text-black-pure">Session Steps</h2>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-grey-secondary transition-colors hover:bg-black/[0.04] hover:text-black"
            onClick={onClose}
            aria-label="Close session steps"
          >
            <Svg Icon={CloseIcon} className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-7 pb-7 sm:px-8">
          <div className="flex flex-col gap-3 py-1">
            {sorted.map((step) => {
            const theme = getLessonStepTheme(step.type);
            const color = LESSON_STEP_THEME_COLORS[theme].primary;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  onSelectStep(step.order);
                  onClose();
                }}
                aria-current={step.order === currentStepOrder ? 'step' : undefined}
                className="flex h-[73px] min-h-[73px] w-full shrink-0 cursor-pointer items-stretch overflow-hidden rounded-2xl border text-left transition duration-200 hover:shadow-[0_6px_20px_rgba(30,30,30,0.16)] hover:brightness-[0.96] active:shadow-[0_2px_8px_rgba(30,30,30,0.1)]"
                style={{ borderColor: color, color }}
              >
                <div
                  className="flex shrink-0 items-center justify-center border-r bg-[#FFFAEE] px-4 text-[14px] font-normal leading-[120%]"
                  style={{ borderColor: color }}
                >
                  Step {step.order}
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 bg-[#FFFAEE] py-2 pl-3 pr-3">
                  <p className="truncate text-base font-medium leading-[120%]">
                    {getLessonStepTitle(step)}
                  </p>
                  {step.description && (
                    <p className="line-clamp-2 text-[14px] font-normal leading-[120%]">
                      {step.description}
                    </p>
                  )}
                </div>
                <div
                  className="flex w-[56px] shrink-0 items-center justify-center"
                  style={{ backgroundColor: color }}
                >
                  <Svg Icon={ArrowRightIcon} className="h-5 w-5 text-[#FFFAEE]" />
                </div>
              </button>
            );
          })}
          </div>
        </div>
      </aside>
    </>
  );
};
